import ArcGisLayer from '@arcgis/core/layers/Layer';
import esriConfig from '@arcgis/core/config';
import { Layer as LayerSettingsLayer } from '../models/layer-settings';
import arcGisFactory from './arcgis-factory';
import { calculateWindowFeatures } from './arcGIS-tools';
import arcgisLoginEvent from '../services/fetch/user-events/arcgis-login-event';

export type ArcGisConnection = (
  esriApiKey: string,
  appId?: string | undefined,
  portalUrl?: string | undefined,
) => ArcGisConnectionObject;

export interface ArcGisConnectionObject {
  getAuthToken: () => Promise<string>;
  destroyCredentials: () => void;
  checkSignInStatus: () => Promise<boolean>;
  fetchAllLayers: () => Promise<LayerSettingsLayer[]>;
  fetchLayer: (layerId: string, layerUrl) => Promise<ArcGisLayer>;
}

export const DEFAULT_PORTAL_URL = 'https://www.arcgis.com';
export const DEFAULT_APP_ID = 'aomkCrDAH4wHfg1S';
export const NUMBER_OF_LAYERS_PER_PAGE = 50;
export const MAX_NUMBER_OF_LAYERS = 10000; // maximum number of layers ArcGIS will return without erroring out

const loadedLayers = {};

export const clearLoadedLayersCache = () => {
  Object.keys(loadedLayers).forEach((key) => {
    delete loadedLayers[key];
  });
};

const arcGisConnection: ArcGisConnection = (
  esriApiKey,
  appId = DEFAULT_APP_ID,
  portalUrl = DEFAULT_PORTAL_URL,
) => {
  const factory = arcGisFactory();
  const { identityManager } = factory;
  let credentials;
  let initialSignIn = false;

  const oAuthInfo = factory.newOAuthInfo({
    appId,
    flowType: 'authorization-code',
    portalUrl,
    popup: true,
    popupCallbackUrl: '/oauth/arcgis/callback',
    popupWindowFeatures: calculateWindowFeatures(),
  });

  identityManager.registerOAuthInfos([oAuthInfo]);

  const isOnline = appId === DEFAULT_APP_ID && portalUrl === DEFAULT_PORTAL_URL;

  const sharingUrl = `${portalUrl}/sharing`;

  const getAuthToken = async (): Promise<string> => {
    const hasCredentials = await checkSignInStatus();

    credentials = await identityManager.getCredential(sharingUrl);

    if (!hasCredentials) {
      initialSignIn = true;
    }

    return credentials.token;
  };

  const destroyCredentials = () => {
    identityManager.destroyCredentials();
  };

  const checkSignInStatus = async () => {
    try {
      await identityManager.checkSignInStatus(sharingUrl);
      return true;
    } catch (_ignored) {
      return false;
    }
  };

  const prepareGlobalSettings = () => {
    if (isOnline) {
      return factory.setEsriApiKey('');
    }
    return factory.setEsriPortalUrl(portalUrl);
  };

  const resetGlobalSettings = () => {
    if (isOnline) {
      return factory.setEsriApiKey(esriApiKey);
    }
    return factory.setEsriPortalUrl(DEFAULT_PORTAL_URL);
  };

  const sendLoginEvent = async (portal) => {
    const sourceJSON = portal.user?.sourceJSON;
    await arcgisLoginEvent({
      portalUrl: isOnline ? undefined : portalUrl,
      esriUserId: sourceJSON?.id,
      esriLicenseTypeId: sourceJSON?.userLicenseTypeId,
      esriOrgId: sourceJSON?.orgId,
    });
    initialSignIn = false;
  };

  const fetchAllLayers = async (): Promise<LayerSettingsLayer[]> => {
    prepareGlobalSettings();

    const portal = factory.newPortal();
    portal.authMode = 'immediate';
    await portal.load();

    const allLayers: any[] = [];

    if (portal.user) {
      if (initialSignIn) {
        sendLoginEvent(portal);
      }
      try {
        const fetchedLayers = await fetchNextPage(1, portal);
        const { total, layers } = fetchedLayers;

        if (layers.length > 0) {
          allLayers.push(...layers);
        }
        if (total >= NUMBER_OF_LAYERS_PER_PAGE) {
          const numberOfLayersToFetch = Math.min(total, MAX_NUMBER_OF_LAYERS);
          const numOfPages = Math.ceil(numberOfLayersToFetch / NUMBER_OF_LAYERS_PER_PAGE);
          const range = [...Array(numOfPages).keys()];
          range.shift();

          const results = await Promise.all(range.map(
            (num) => fetchNextPage((NUMBER_OF_LAYERS_PER_PAGE * num) + 1, portal),
          ));

          results.forEach((result) => {
            if (result.layers.length > 0) {
              allLayers.push(...result.layers);
            }
          });
        }
      } catch (error) {
        console.warn('Error fetching layers:', error);
        allLayers.length = 0;
      }
    }

    resetGlobalSettings();

    return allLayers;
  };

  // we assume that even though we only ask for Feature Services that we may get more than that back
  const filterAndFormat = (results: any[], username: string): LayerSettingsLayer[] => results
    .filter((result) => result.type === 'Feature Service')
    .map((layer) => (
      {
        id: layer.id,
        name: layer.title,
        type: 'feature-service',
        description: layer.description || '',
        origin: 'arcgis',
        url: layer.url,
        esriApiKey: isOnline ? undefined : '',
        esriAppId: isOnline ? undefined : appId,
        esriPortalUrl: isOnline ? undefined : portalUrl,
        esriUsername: username,
        baseURL: layer.itemUrl,
      }
    ));

  const fetchNextPage = async (start, portal): Promise<{ total: number, layers: LayerSettingsLayer[] }> => {
    const { user: { username, orgId } } = portal;

    const queryParams = factory.newPortalQueryParams({
      query: `(type:"Feature Service") AND (orgid:${orgId} OR owner:${username} OR access:shared)`,
      sortField: 'num-views',
      sortOrder: 'desc',
      num: NUMBER_OF_LAYERS_PER_PAGE,
      start,
    });

    const response = await portal.queryItems(queryParams) || { results: [] };

    if (response.results.length > 0) {
      return { total: response.total, layers: filterAndFormat(response.results, username) };
    }
    return { total: response.total, layers: [] };
  };

  const fetchLayer = async (layerId: string, baseURL: string): Promise<ArcGisLayer> => {
    const token = await getAuthToken();

    if (!loadedLayers[token]) {
      loadedLayers[token] = {};
    }

    if (loadedLayers[token][layerId]) {
      return loadedLayers[token][layerId];
    }

    if (isOnline && esriConfig.apiKey !== '') {
      factory.setEsriApiKey('');
    }
    const portalItem = factory.fromPortalItem({
      portalItem: {
        id: layerId,
        apiKey: token,
        portal: {
          url: baseURL,
        },
      },
    });

    loadedLayers[token][layerId] = portalItem;

    return portalItem;
  };

  return {
    getAuthToken, destroyCredentials, checkSignInStatus, fetchAllLayers, fetchLayer,
  };
};

export default arcGisConnection;
